import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articlesData } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

function isHeading(para: string): boolean {
  const trimmed = para.trim();
  if (!trimmed || trimmed.length > 100) return false;
  if (/^Step \d+:/.test(trimmed)) return true;
  if (trimmed.includes(".")) return false;
  return true;
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = articlesData.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.icu";
    const articleUrl = `${siteUrl}/blog/${article.slug}`;

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "Kakobuy Spreadsheet Hub", url: siteUrl },
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    });
    document.head.appendChild(schemaScript);

    const bcScript = document.createElement("script");
    bcScript.type = "application/ld+json";
    bcScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${siteUrl}/blog` },
        { "@type": "ListItem", "position": 3, "name": article.title, "item": articleUrl },
      ]
    });
    document.head.appendChild(bcScript);

    return () => {
      document.head.removeChild(schemaScript);
      document.head.removeChild(bcScript);
    };
  }, [article]);

  if (!article) {
    return <NotFound />;
  }

  const paragraphs = article.content.split("\n\n");

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative min-h-[400px] md:min-h-[480px] flex items-end overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        <div className="relative z-10 container-wide py-12 pt-32 md:pt-40">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-medium text-primary-400 uppercase tracking-wider mb-3">
              Buying Guide
            </span>
            <h1 className="hero-h1 text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-4 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 text-sm text-white/60">
              <span>By {article.author}</span>
              <span>|</span>
              <span>Updated {article.date}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="bg-background-100 border-b border-background-200" aria-label="Breadcrumb">
        <div className="container-wide py-3">
          <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-600">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            </li>
            <li><i className="ri-arrow-right-s-line text-foreground-400" /></li>
            <li>
              <Link to="/blog" className="hover:text-primary-600 transition-colors">Blog</Link>
            </li>
            <li><i className="ri-arrow-right-s-line text-foreground-400" /></li>
            <li className="text-foreground-900 font-medium">{article.title}</li>
          </ol>
        </div>
      </nav>

      {/* Article Content */}
      <article className="container-wide py-10 md:py-14 lg:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-sm md:prose-base max-w-none">
            {paragraphs.map((para, idx) => {
              if (isHeading(para)) {
                return (
                  <h2 key={idx} className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mt-8 mb-4">
                    {para}
                  </h2>
                );
              }
              return (
                <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  {para}
                </p>
              );
            })}
          </div>

          {/* FAQ in Article */}
          {article.faqs.length > 0 && (
            <>
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mt-8 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3 mb-8">
                {article.faqs.map((faq, idx) => (
                  <div key={idx} className="bg-white rounded-xl border border-background-200 overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer"
                    >
                      <span className="font-medium text-sm md:text-base text-foreground-900 pr-4">{faq.question}</span>
                      <i className={`ri-arrow-down-s-line text-lg text-primary-500 transition-transform shrink-0 ${openFaq === idx ? "rotate-180" : ""}`} />
                    </button>
                    {openFaq === idx && (
                      <div className="px-5 pb-4 md:px-6 md:pb-5">
                        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Article CTA */}
          <div className="bg-primary-50 rounded-xl p-6 md:p-8 mb-8">
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">
              Ready to Shop with the Kakobuy Spreadsheet?
            </h3>
            <p className="text-sm md:text-base text-foreground-700 mb-4">
              Browse the full kakobuy spreadsheet with 500+ verified products and trusted sellers.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="btn-accent text-sm">
                <span className="flex items-center gap-2">
                  Buy It Now
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-home-line" />
                Back to Home
              </Link>
            </div>
          </div>

          {/* Internal Links - Related Articles */}
          <div className="border-t border-background-200 pt-8">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {articlesData
                .filter((a) => a.slug !== article.slug)
                .slice(0, 3)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="group bg-white rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={related.image} alt={related.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
            </div>
          </div>

          {/* Author Box */}
          <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6 mt-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                <i className="ri-user-line text-primary-600 text-xl md:text-2xl" />
              </div>
              <div>
                <p className="font-semibold text-sm md:text-base text-foreground-900">Written by {article.author}</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1 leading-relaxed">
                  All recommendations in our kakobuy spreadsheet are independently researched and updated regularly. Our team reviews seller feedback, QC photos, and shipping data to provide accurate and trustworthy information.
                </p>
                <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                  <span>Last Updated: {article.date}</span>
                  <span>|</span>
                  <span>Editorial Policy: Independent Research</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}